import React, { useEffect, useMemo, useRef } from 'react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TaskFormFields } from 'components/AppKanbanBoard/config'
import { RichTextInput } from 'components/Chat/components/RichTextInput/RichTextInput'
import { useGetTeammember } from 'hooks/useGetTeammember'
import { sanitizeRichTextHtml } from 'appUtils/sanitizeRichTextHtml'

interface IAppTaskDescriptionFieldProps {
  methods: UseFormReturn
  clickedFields: { [key: string]: boolean }
  handleClick: (field: string, isHovered: boolean) => void
}

const looksLikeHtml = (value: string): boolean => /<[a-z][\s\S]*>/i.test(value || '')

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * A plain-text description (Coach and older tools write them) as editor HTML: its characters stay
 * as written, a blank line starts a paragraph and a single line break stays a line break.
 */
const plainTextToHtml = (text: string): string =>
  text
    .replace(/\r\n?/g, '\n')
    .trim()
    .split(/\n\s*\n/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('')

export const AppTaskDescriptionField: React.FC<IAppTaskDescriptionFieldProps> = ({
  methods,
  clickedFields,
  handleClick,
}) => {
  const descriptionWrapperRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()

  const { control } = methods
  const teamMembers = useGetTeammember()

  const mentionableMembers = useMemo(
    () =>
      teamMembers.map((m) => ({
        id: m.id,
        label: `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'User',
        email: '',
        firstName: m.firstName || '',
        lastName: m.lastName || '',
      })),
    [teamMembers]
  )

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        descriptionWrapperRef.current &&
        !descriptionWrapperRef.current.contains(event.target as Node)
      ) {
        // Do not collapse while a popup / overlay from the editor is open
        const target = event.target as HTMLElement | null
        if (
          target?.closest?.('.tippy-box, [data-emoji-picker], [data-radix-popper-content-wrapper]')
        )
          return
        handleClick(TaskFormFields.instructionForTask, false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [handleClick])

  return (
    <Controller
      name={TaskFormFields.instructionForTask}
      control={control}
      render={({ field }) => {
        const rawValue: string = field.value || ''
        const htmlValue = rawValue
          ? looksLikeHtml(rawValue)
            ? rawValue
            : plainTextToHtml(rawValue)
          : ''

        return (
          <>
            {!clickedFields.instructionForTask ? (
              <div
                className="font-inter text-sm text-app-black-90 rounded-lg cursor-text min-h-[40px] prose prose-sm max-w-none [&_a]:text-app-blue-600 [&_a]:underline hover:[&_a]:text-app-blue-800 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
                onClick={() => handleClick(field.name, true)}
              >
                {rawValue ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichTextHtml(htmlValue, { mentions: true }),
                    }}
                  />
                ) : (
                  <span className="text-app-grey-60">
                    {t('playboard.playForm.typePlayDescription')}
                  </span>
                )}
              </div>
            ) : (
              <div ref={descriptionWrapperRef}>
                <RichTextInput
                  mode="form"
                  placeholder={t('playboard.playForm.typePlayDescription')}
                  value={htmlValue}
                  onChange={(html) => field.onChange(html)}
                  mentionProvider={(query: string) => {
                    const q = (query || '').toLowerCase()
                    return mentionableMembers.filter((i) => i.label.toLowerCase().includes(q))
                  }}
                  initialMentions={mentionableMembers}
                />
              </div>
            )}
          </>
        )
      }}
    />
  )
}
